import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Briefcase, Building2, Lock, SearchX, Sparkles } from "lucide-react";
import { lookbookApi } from "@/api/lookbook-api";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MultiSelect } from "@/components/ui/multi-select";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  CoverEyebrow,
  fundCover,
  PageContainer,
  PeopleStack,
  PublicCover,
  PublicState,
  RaiseBoardNav,
  RaiseBoardShell,
} from "./rb-ui";
import {
  BoardCard,
  BoardCardSkeleton,
  BoardList,
  BoardRow,
  BoardRowSkeleton,
} from "./board-row";
import {
  BoardGrid,
  CollectionToolbar,
  SortSelect,
  ViewToggle,
  sortApplications,
  useSort,
  useViewMode,
} from "./board-collection";
import {
  getApplicantCompanyId,
  getFilterValues,
  matchesFilter,
  uniqueSorted,
  type Application,
} from "./deal-fields";
import { useMyVotes, useToggleUpvote } from "./use-raise-board-actions";
import { SubscribeButton } from "./subscribe-button";
import { InviteButton } from "./invite-button";

/**
 * A lookbook — a private raiseboard, reachable only by its link.
 *
 * Deliberately the same reading experience as the global board: same masthead,
 * same filters, same cards. What differs is that the deals are one workspace's
 * hand-picked selection, and the upvotes and comments shown are the lookbook's
 * own rather than the public tallies (the server swaps those in, so the shared
 * card needs no say in it).
 */
export default function LookbookPublic() {
  const { lookbookPublicId } = useParams<{ lookbookPublicId: string }>();
  const navigate = useNavigate();
  const { isSignedIn } = useUser();

  const [industryFilter, setIndustryFilter] = useState<string[]>([]);
  const [businessModelFilter, setBusinessModelFilter] = useState<string[]>([]);
  const [authReason, setAuthReason] = useState<string | null>(null);
  const [view, setView] = useViewMode("lookbook");
  const [sort, setSort] = useSort("endorsements");

  const { data, isLoading, error } = useQuery({
    queryKey: ["lookbook", lookbookPublicId],
    queryFn: () => lookbookApi.getByPublicId(lookbookPublicId as string),
    enabled: !!lookbookPublicId,
    retry: false,
    refetchOnWindowFocus: true,
  });

  // A signed-out visitor votes as a guest when the owner allows it; signing in
  // always wins, so a member's vote is never filed under their browser id.
  const guest = !isSignedIn && data?.result?.lookbook?.guest_engagement === true;
  const votedIds = useMyVotes(!!isSignedIn || guest, lookbookPublicId, guest);
  const toggleVote = useToggleUpvote({ lookbookPublicId, guest });

  const handleVote = (application: Application) => {
    if (!isSignedIn && !guest) {
      setAuthReason("Sign in to upvote this deal.");
      return;
    }
    toggleVote.mutate(String(application._id));
  };

  const openDeal = (application: Application) => {
    const applicantId = getApplicantCompanyId(application);
    if (!applicantId) {
      toast.error("This deal's profile isn't available.");
      return;
    }
    // Stays inside the lookbook rather than routing through /rb/:slug — the
    // deal's own board may not even be public, and the visitor's access came
    // from this link.
    navigate(`/lb/${lookbookPublicId}/${applicantId}`);
  };

  const applications = useMemo(
    () => data?.result?.applications ?? [],
    [data]
  );
  const lookbookName = data?.result?.lookbook?.name ?? "Lookbook";
  const ownerName = data?.result?.opportunity?.opportunity_name;

  const filterOptions = useMemo(() => {
    const industries: string[] = [];
    const businessModels: string[] = [];
    for (const app of applications) {
      const v = getFilterValues(app);
      industries.push(...v.industries);
      businessModels.push(...v.businessModels);
    }
    return {
      industries: uniqueSorted(industries),
      businessModels: uniqueSorted(businessModels),
    };
  }, [applications]);

  const filtered = useMemo(() => {
    return applications.filter((app) => {
      const v = getFilterValues(app);
      return (
        matchesFilter(v.industries, industryFilter) &&
        matchesFilter(v.businessModels, businessModelFilter)
      );
    });
  }, [applications, industryFilter, businessModelFilter]);


  const sorted = useMemo(() => sortApplications(filtered, sort), [filtered, sort]);

  const totalUpvotes = useMemo(
    () => applications.reduce((sum, a) => sum + (a.upvote_count ?? 0), 0),
    [applications]
  );

  const toOptions = (values: string[]) =>
    values.map((v) => ({ label: v, value: v }));

  const authDialog = (
    <BoardAuthDialog
      open={!!authReason}
      onOpenChange={(open) => {
        if (!open) setAuthReason(null);
      }}
      reason={authReason ?? undefined}
    />
  );

  if (error) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={setAuthReason} />
        <PublicCover
          size="lg"
          eyebrow={
            <>
              <Lock />
              Private lookbook
            </>
          }
          title="This lookbook isn't available"
          lede="The link may have expired, or the fund that shared it has taken it offline. If you were expecting to see it, ask them for a new link."
        />
        {authDialog}
      </RaiseBoardShell>
    );
  }

  const opportunity = data?.result?.opportunity;
  const brand = ownerName
    ? {
        name: ownerName,
        logoUrl: (opportunity as { logo_url?: string } | undefined)?.logo_url,
        href:
          opportunity?.raise_board_enabled && opportunity?.portal_slug
            ? `/rb/${opportunity.portal_slug}`
            : undefined,
        context: "Lookbook",
      }
    : undefined;
  const team = opportunity?.team_members ?? [];
  const cover = fundCover(opportunity);
  const curators = team
    .map((p) => p.first_name)
    .filter(Boolean)
    .slice(0, 2) as string[];
  const companyWord = applications.length === 1 ? "company" : "companies";

  return (
    <TooltipProvider delayDuration={200}>
      <RaiseBoardShell
        footerNote={
          ownerName
            ? `Shared privately by ${ownerName}. Please keep this link to yourself.`
            : "Shared privately. Please keep this link to yourself."
        }
      >
        <RaiseBoardNav onRequestAuth={setAuthReason} brand={brand} background={cover} />

        <PublicCover
          background={cover}
          eyebrow={
            <CoverEyebrow
              items={[
                <>
                  <Lock />
                  Private lookbook
                </>,
                "By invitation",
              ]}
            />
          }
          title={
            isLoading ? (
              <Skeleton className="h-[0.9em] w-[7ch] rounded-lg" />
            ) : (
              lookbookName
            )
          }
          lede={
            isLoading ? (
              <Skeleton className="h-5 w-72" />
            ) : (
              <>
                <strong>
                  {applications.length} {companyWord}
                </strong>
                , chosen by hand{ownerName ? ` at ${ownerName}` : ""} and shared
                with you directly.
                {totalUpvotes > 0 &&
                  ` Endorsed ${totalUpvotes} ${totalUpvotes === 1 ? "time" : "times"} so far.`}
              </>
            )
          }
          aside={
            <>
              {ownerName && team.length > 0 && (
                <div className="flex items-center gap-3">
                  <PeopleStack people={team} />
                  <p className="text-[13px] text-muted-foreground">
                    Curated by{" "}
                    <span className="text-foreground">
                      {curators.length > 0
                        ? `${curators.join(" and ")} at ${ownerName}`
                        : `the ${ownerName} team`}
                    </span>
                  </p>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <SubscribeButton
                  lookbookPublicId={lookbookPublicId}
                  onRequestAuth={setAuthReason}
                  className="h-10 rounded-lg px-4 text-[13.5px] [&_svg]:h-3.5 [&_svg]:w-3.5"
                />
                {/* Owners only — renders nothing for anyone else. */}
                <InviteButton
                  opportunityId={opportunity?._id}
                  lookbookId={data?.result?.lookbook?._id}
                  passwordProtected={data?.result?.lookbook?.password_protected}
                  className="h-10 rounded-lg bg-card px-4 text-[13.5px] [&_svg]:h-3.5 [&_svg]:w-3.5"
                />
              </div>
            </>
          }
        />

        <PageContainer className="pt-10 sm:pt-12">
          <section aria-label="Companies">
            <CollectionToolbar
              title="The selection"
              count={isLoading ? undefined : filtered.length}
              sort={<SortSelect value={sort} onChange={setSort} />}
              view={<ViewToggle value={view} onChange={setView} />}
              filters={
                <>
                  <MultiSelect
                    id="lookbook-industry"
                    options={toOptions(filterOptions.industries)}
                    value={industryFilter}
                    onValueChange={setIndustryFilter}
                    placeholder="Industry"
                    triggerIcon={Building2}
                    maxCount={2}
                    className="h-9 w-auto min-w-[140px] rounded-lg border-border bg-card text-[13px]"
                  />
                  <MultiSelect
                    id="lookbook-business-model"
                    options={toOptions(filterOptions.businessModels)}
                    value={businessModelFilter}
                    onValueChange={setBusinessModelFilter}
                    placeholder="Business model"
                    triggerIcon={Briefcase}
                    maxCount={2}
                    className="h-9 w-auto min-w-[160px] rounded-lg border-border bg-card text-[13px]"
                  />
                </>
              }
            />

            {isLoading ? (
              view === "grid" ? (
                <BoardGrid>
                  {[0, 1, 2].map((i) => (
                    <BoardCardSkeleton key={i} />
                  ))}
                </BoardGrid>
              ) : (
                <BoardList>
                  {[0, 1, 2, 3].map((i) => (
                    <BoardRowSkeleton key={i} />
                  ))}
                </BoardList>
              )
            ) : applications.length === 0 ? (
              <PublicState
                icon={<Sparkles />}
                title="Nothing here yet"
                className="border-t border-foreground"
              >
                {ownerName ?? "The fund"} hasn&rsquo;t added any companies to this
                lookbook yet. Subscribe and you&rsquo;ll hear the moment they do.
              </PublicState>
            ) : sorted.length === 0 ? (
              <PublicState
                icon={<SearchX />}
                title="No companies match those filters"
                className="border-t border-foreground"
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 rounded-lg bg-card"
                    onClick={() => {
                      setIndustryFilter([]);
                      setBusinessModelFilter([]);
                    }}
                  >
                    Clear filters
                  </Button>
                }
              />
            ) : view === "grid" ? (
              <BoardGrid>
                {sorted.map((application, index) => (
                  <BoardCard
                    key={String(application._id)}
                    application={application}
                    rank={index + 1}
                    index={index}
                    vote={{
                      voted: votedIds.has(String(application._id)),
                      count: application.upvote_count ?? 0,
                    }}
                    onVote={() => handleVote(application)}
                    onOpen={() => openDeal(application)}
                    showSource={false}
                  />
                ))}
              </BoardGrid>
            ) : (
              <BoardList>
                {sorted.map((application, index) => (
                  <BoardRow
                    key={String(application._id)}
                    application={application}
                    rank={index + 1}
                    index={index}
                    vote={{
                      voted: votedIds.has(String(application._id)),
                      count: application.upvote_count ?? 0,
                    }}
                    onVote={() => handleVote(application)}
                    onOpen={() => openDeal(application)}
                    showSource={false}
                  />
                ))}
              </BoardList>
            )}
          </section>
        </PageContainer>

        {authDialog}
      </RaiseBoardShell>
    </TooltipProvider>
  );
}
